<template>
  <v-container fluid>
    <!-- Page Header .-->
    <v-row class="mb-4">
      <v-col>
        <div class="d-flex align-center justify-space-between">
          <div>
            <h1 class="text-h4 font-weight-bold mb-2">Audit Trail</h1>
            <p class="text-subtitle-1 text-grey-darken-1 mb-0">
              Track all system activities and user actions
            </p>
          </div>
          <div>
            <v-chip color="primary" variant="elevated" prepend-icon="mdi-file-document">
              {{ totalItems }} entries
            </v-chip>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Filters Card -->
    <v-card class="mb-4" elevation="1">
      <v-card-title class="bg-grey-lighten-4 py-3">
        <v-icon class="mr-2">mdi-filter-variant</v-icon>
        Filters
      </v-card-title>
      <v-card-text class="pt-4">
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.userFilter"
              label="User Name"
              prepend-inner-icon="mdi-account-search"
              variant="outlined"
              density="compact"
              clearable
              @input="debouncedSearch"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.codeFilter"
              label="Action Type"
              :items="auditCodes"
              item-title="auditMessage"
              item-value="auditCode"
              prepend-inner-icon="mdi-code-tags"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="loadAuditTrail"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.fromDate"
              label="From Date"
              type="date"
              prepend-inner-icon="mdi-calendar"
              variant="outlined"
              density="compact"
              @change="loadAuditTrail"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.toDate"
              label="To Date"
              type="date"
              prepend-inner-icon="mdi-calendar"
              variant="outlined"
              density="compact"
              @change="loadAuditTrail"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-btn
              color="primary"
              variant="outlined"
              prepend-icon="mdi-refresh"
              @click="resetFilters"
            >
              Reset Filters
            </v-btn>
            <v-btn
              color="success"
              variant="outlined"
              prepend-icon="mdi-download"
              class="ml-2"
              @click="exportData"
            >
              Export
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Main Data Grid -->
    <v-card elevation="1">
      <v-card-title class="bg-primary text-white d-flex align-center py-3">
        <v-icon class="mr-2">mdi-clipboard-text-clock</v-icon>
        Audit Trail Entries
        <v-spacer />
        <v-btn
          icon="mdi-refresh"
          variant="text"
          color="white"
          size="small"
          @click="loadAuditTrail"
        />
      </v-card-title>

      <v-data-table-server
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="auditTrail"
        :items-length="totalItems"
        :loading="loading"
        class="elevation-0"
        density="comfortable"
        @update:options="handleTableUpdate"
      >
        <!-- User Column with Avatar -->
        <template #item.userName="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" class="mr-3" :color="getUserColor(item.userName)">
              <span class="white--text text-caption font-weight-bold">
                {{ getUserInitials(item.userName) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.userName || "System" }}</div>
              <div class="text-caption text-grey-darken-1">
                {{
                  item.userId ? "User ID: " + item.userId.substring(0, 8) + "..." : "System Action"
                }}
              </div>
            </div>
          </div>
        </template>

        <!-- Date Time Column -->
        <template #item.dateTime="{ item }">
          <div class="text-no-wrap">
            <div class="font-weight-medium">
              {{ formatDate(item.dateTime) }}
            </div>
            <div class="text-caption text-grey-darken-1">
              {{ formatTime(item.dateTime) }}
            </div>
          </div>
        </template>

        <!-- Audit Code Column -->
        <template #item.auditCode="{ item }">
          <v-chip
            size="small"
            :color="getAuditCodeColor(item.auditCode)"
            variant="elevated"
            class="font-weight-medium"
          >
            {{ item.auditCode }}
          </v-chip>
        </template>

        <!-- Action/Message Column -->
        <template #item.auditMessage="{ item }">
          <div class="py-2">
            <div class="font-weight-medium mb-1">
              {{ getAuditMessage(item.auditCode) }}
            </div>
            <div v-if="item.annotation" class="text-body-2 text-grey-darken-1">
              <v-icon size="12" class="mr-1">mdi-note-text</v-icon>
              {{ item.annotation }}
            </div>
            <div v-if="item.entityType" class="text-caption text-primary mt-1">
              <v-icon size="12" class="mr-1">mdi-link</v-icon>
              {{ item.entityType }}: {{ item.entityId }}
            </div>
          </div>
        </template>

        <!-- IP Address Column -->
        <template #item.ipAddress="{ item }">
          <div v-if="item.ipAddress" class="d-flex align-center">
            <v-icon size="16" class="mr-1 text-grey-darken-1">mdi-ip-network</v-icon>
            <span class="text-body-2 font-mono">{{ item.ipAddress }}</span>
          </div>
          <span v-else class="text-grey-darken-2">-</span>
        </template>

        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <div class="d-flex">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="primary"
              @click="viewDetails(item)"
            />
            <v-btn
              icon="mdi-content-copy"
              size="small"
              variant="text"
              color="grey"
              @click="copyToClipboard(item)"
            />
          </div>
        </template>

        <!-- Loading State -->
        <template #loading>
          <v-skeleton-loader type="table-row@10" />
        </template>

        <!-- No Data State -->
        <template #no-data>
          <div class="text-center pa-8">
            <v-icon size="64" color="grey-lighten-2" class="mb-4">
              mdi-clipboard-text-clock
            </v-icon>
            <h3 class="text-h6 mb-2">No audit entries found</h3>
            <p class="text-body-2 text-grey-darken-1 mb-4">
              {{
                filters.userFilter || filters.codeFilter || filters.fromDate
                  ? "Try adjusting your filters to see more results"
                  : "No audit trail entries have been recorded yet"
              }}
            </p>
            <v-btn
              v-if="filters.userFilter || filters.codeFilter || filters.fromDate"
              color="primary"
              variant="outlined"
              @click="resetFilters"
            >
              Clear Filters
            </v-btn>
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="700">
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-information-outline</v-icon>
          Audit Entry Details
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="white" @click="detailsDialog = false" />
        </v-card-title>

        <v-card-text v-if="selectedItem" class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1">AUDIT ID</label>
                <div class="text-h6">{{ selectedItem.auditId }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1">AUDIT CODE</label>
                <div>
                  <v-chip
                    :color="getAuditCodeColor(selectedItem.auditCode)"
                    size="small"
                    class="mt-1"
                    variant="elevated"
                  >
                    {{ selectedItem.auditCode }}
                  </v-chip>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1">USER</label>
                <div class="d-flex align-center mt-1">
                  <v-avatar size="24" class="mr-2" :color="getUserColor(selectedItem.userName)">
                    <span class="white--text text-caption">{{
                      getUserInitials(selectedItem.userName)
                    }}</span>
                  </v-avatar>
                  {{ selectedItem.userName || "System" }}
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1">DATE & TIME</label>
                <div class="text-body-1">{{ formatDateTime(selectedItem.dateTime) }}</div>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1"
                  >ACTION PERFORMED</label
                >
                <div class="text-body-1">{{ getAuditMessage(selectedItem.auditCode) }}</div>
              </div>
            </v-col>
            <v-col cols="12" v-if="selectedItem.annotation">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1"
                  >ADDITIONAL NOTES</label
                >
                <div class="text-body-1">{{ selectedItem.annotation }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedItem.ipAddress">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1">IP ADDRESS</label>
                <div class="text-body-1 font-mono">{{ selectedItem.ipAddress }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedItem.entityType">
              <div class="mb-4">
                <label class="text-caption font-weight-bold text-grey-darken-1"
                  >RELATED ENTITY</label
                >
                <div class="text-body-1">
                  {{ selectedItem.entityType }}: {{ selectedItem.entityId }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn color="grey" variant="outlined" @click="detailsDialog = false"> Close </v-btn>
          <v-btn color="primary" @click="copyToClipboard(selectedItem)">
            <v-icon class="mr-1">mdi-content-copy</v-icon>
            Copy Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { fetchAuditTrail, fetchAuditCodes, logAuditEvent } from "@/api/audit";

// Create a simple debounce function instead of importing lodash
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ... rest of your code stays exactly the same ...
const auditTrail = ref([]);
const auditCodes = ref([]);
const loading = ref(false);
const totalItems = ref(0);
const page = ref(1);
const itemsPerPage = ref(25);
const sortBy = ref([{ key: "dateTime", order: "desc" }]);
const detailsDialog = ref(false);
const selectedItem = ref(null);

// Filters
const filters = ref({
  userFilter: "",
  codeFilter: "",
  fromDate: "",
  toDate: "",
});

const loadAuditTrail = async () => {
  loading.value = true;
  try {
    const data = await fetchAuditTrail({
      page: page.value,
      pageSize: itemsPerPage.value,
      userFilter: filters.value.userFilter || null,
      codeFilter: filters.value.codeFilter || null,
      fromDate: filters.value.fromDate || null,
      toDate: filters.value.toDate || null,
    });

    auditTrail.value = data.data || [];
    totalItems.value = data.totalCount || 0;
  } catch (error) {
    console.error("Error loading audit trail", error);
    auditTrail.value = [];
    totalItems.value = 0;
  } finally {
    loading.value = false;
  }
};

const loadAuditCodes = async () => {
  try {
    auditCodes.value = await fetchAuditCodes();
  } catch (error) {
    console.error("Error loading audit codes", error);
    auditCodes.value = [];
  }
};

// Table headers
const headers = [
  { title: "ID", key: "auditId", sortable: true, width: "80px" },
  { title: "User", key: "userName", sortable: true, width: "200px" },
  { title: "Date/Time", key: "dateTime", sortable: true, width: "160px" },
  { title: "Code", key: "auditCode", sortable: true, width: "100px" },
  { title: "Action", key: "auditMessage", sortable: false, width: "300px" },
  { title: "IP Address", key: "ipAddress", sortable: false, width: "140px" },
  { title: "Actions", key: "actions", sortable: false, width: "100px", align: "center" },
];

const handleTableUpdate = (options) => {
  page.value = options.page;
  itemsPerPage.value = options.itemsPerPage;
  if (options.sortBy?.length > 0) {
    sortBy.value = options.sortBy;
  }
  loadAuditTrail();
};

const debouncedSearch = debounce(() => {
  page.value = 1;
  loadAuditTrail();
}, 500);

const resetFilters = () => {
  filters.value = {
    userFilter: "",
    codeFilter: "",
    fromDate: "",
    toDate: "",
  };
  page.value = 1;
  loadAuditTrail();
};

const viewDetails = (item) => {
  selectedItem.value = item;
  detailsDialog.value = true;
};

const copyToClipboard = async (item) => {
  const text = `Audit ID: ${item.auditId}\nUser: ${item.userName}\nAction: ${getAuditMessage(
    item.auditCode
  )}\nDate: ${formatDateTime(item.dateTime)}\nIP: ${item.ipAddress || "N/A"}`;

  try {
    await navigator.clipboard.writeText(text);
    console.log("Copied to clipboard");
  } catch (err) {
    console.error("Failed to copy: ", err);
  }
};

const exportData = () => {
  console.log("Export functionality to be implemented");
};

const getUserInitials = (userName) => {
  if (!userName) return "SY";
  return userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

const getUserColor = (userName) => {
  if (!userName) return "grey";
  const colors = ["primary", "secondary", "success", "info", "warning", "error"];
  const hash = userName.split("").reduce((a, b) => a + b.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (dateString) => {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getAuditCodeColor = (code) => {
  const colorMap = {
    AUD001: "success", // Login
    AUD002: "warning", // Logout
    AUD003: "info", // Dashboard
    AUD004: "primary", // Create
    AUD005: "secondary", // Edit
    AUD006: "error", // Delete
    AUD007: "error", // Delete confirmation
  };
  return colorMap[code] || "grey";
};

const getAuditMessage = (code) => {
  const auditCode = auditCodes.value.find((ac) => ac.auditCode === code);
  return auditCode?.auditMessage || `Action ${code}`;
};

// Lifecycle
onMounted(() => {
  loadAuditCodes();
  loadAuditTrail();
});
</script>

<style scoped>
.font-mono {
  font-family: "Courier New", monospace;
}

.text-no-wrap {
  white-space: nowrap;
}

.v-data-table :deep(.v-data-table__td) {
  padding: 8px 16px;
}

.v-skeleton-loader {
  background: transparent;
}

label {
  display: block;
  margin-bottom: 4px;
}
</style>
